import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { SCREEN_TYPES } from 'utils/constants';
import { useAppStore, useProductStore, useReceiptStore } from 'store';

const colors = {
  nib: 'bg-green-200 text-green-800', // Green - Good
  nob: 'bg-yellow-100 text-yellow-700', // Yellow - Warning
  usable: 'bg-blue-200 text-blue-700', // Blue - Info
  salvage: 'bg-orange-200 text-orange-700', // Orange - Critical
};

const getDispositionColor = (stage?: string) => {
  return colors[stage as keyof typeof colors] || 'bg-slate-200 text-slate-700';
};

export default function Disposition() {
  const { setCurrentScreen } = useAppStore();
  const { setCurrentProduct, currentProduct: productLine } = useProductStore();
  const { receiptData } = useReceiptStore();

  const onComplete = (): void => {
    setCurrentProduct(null);

    const lines = receiptData?.lines ?? [];
    const expectedTotal = lines.filter((l) => l?.expected_ind === 'y').length;
    const expectedGraded = lines.filter((l) => l?.expected_ind === 'y' && l?.receipt_ind === 'y').length;

    if (expectedTotal > 0 && expectedGraded >= expectedTotal) {
      setCurrentScreen(SCREEN_TYPES.CONFIRM_CLOSE);
    } else {
      setCurrentScreen(SCREEN_TYPES.SCAN_BOX);
    }
  };

  const handleConfirm = (): void => {
    onComplete();
  };

  return (
    <div className="space-y-4">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900">
        Disposition
      </CardTitle>

      <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">
            Tracking #
          </span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {receiptData?.tracking_id || receiptData?.receipt?.tracking_id}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">
            Article #
          </span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {productLine?.product_id}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">
            Desc:
          </span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {productLine?.description || 'N/A'}
          </span>
        </div>

        <div className="flex flex-col gap-1 border-b border-slate-100 py-1.5">
          <span className="text-sm font-semibold text-slate-600">
            Color/Size:
          </span>
          <span className="break-all text-sm font-semibold text-slate-900">
            {productLine?.style_color || productLine?.style_color_name || 'N/A'} -{' '}
            {productLine?.product_size || productLine?.variation_size || 'N/A'}
          </span>
        </div>
      </div>

      <div className="text-sm font-bold tracking-wide text-slate-600">MOVE TO</div>

      <div className="text-center">
        <div
          className={`inline-block rounded-full px-4 py-2 text-sm font-semibold uppercase tracking-[0.6px] shadow-sm ${getDispositionColor(
            productLine?.disposition_stage || productLine?.receipt_grade
          )}`}
        >
          {productLine?.disposition_stage || productLine?.receipt_grade || 'N/A'}
        </div>
      </div>

      {productLine?.style_image_url && (
        <div className="my-0.5 flex min-h-[200px] max-h-[250px] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-3 text-lg font-semibold text-slate-400 shadow-sm">
          <img
            src={productLine.style_image_url}
            alt="Product"
            className="max-h-[250px] max-w-full object-contain"
          />
        </div>
      )}

      <div className="buttons-container !justify-center">
        <Button
          type="button"
          onClick={handleConfirm}
          className="h-12 w-full rounded-xl bg-blue-600 px-10 text-base font-bold text-white shadow-[0_4px_12px_rgba(0,123,255,0.35)] hover:bg-blue-700"
        >
          CONFIRM
        </Button>
      </div>
    </div>
  );
}
